import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useNumbers } from '../context/NumberContext';
import { loadAiConfig, AI_PROVIDERS } from './Settings';

const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const LANGUAGES = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'en_IN', label: 'English (IN)' },
  { code: 'hi',    label: 'Hindi' },
  { code: 'mr',    label: 'Marathi' },
  { code: 'gu',    label: 'Gujarati' },
  { code: 'ta',    label: 'Tamil' },
  { code: 'te',    label: 'Telugu' },
  { code: 'bn',    label: 'Bengali' },
  { code: 'pa',    label: 'Punjabi' },
  { code: 'es',    label: 'Spanish' },
  { code: 'pt_BR', label: 'Portuguese (BR)' },
  { code: 'ar',    label: 'Arabic' },
  { code: 'id',    label: 'Indonesian' },
];

const EMPTY_FORM = {
  name: '', category: 'UTILITY', language: 'en_US',
  header_type: 'none', header_text: '',
  body_text: '', footer_text: '',
  body_examples: [],
  buttons: [],
};

const statusTag = (s) => {
  if (s === 'APPROVED')  return 'tag-green';
  if (s === 'REJECTED' || s === 'PAUSED' || s === 'DISABLED') return 'tag-red';
  if (s === 'PENDING' || s === 'IN_APPEAL' || s === 'IN_REVIEW') return 'tag-yellow';
  return 'tag-gray';
};

const qualityColor = (q) => {
  if (!q) return 'var(--text3)';
  if (q === 'GREEN' || q === 'HIGH')    return 'var(--accent)';
  if (q === 'YELLOW' || q === 'MEDIUM') return 'var(--warn)';
  if (q === 'RED' || q === 'LOW')       return 'var(--danger)';
  return 'var(--text3)';
};

const VIEW_TABS = ['ALL', 'MARKETING', 'UTILITY', 'AUTHENTICATION'];

// ── AI Generation Helper ─────────────────────────────────
const SYSTEM_PROMPT = `You are a WhatsApp Business template expert. Generate templates strictly following Meta's guidelines.

Rules:
- MARKETING: promotions, offers, product launches. Can be engaging/sales-y.
- UTILITY: transactional only (order updates, alerts, reminders). MUST NOT have any promotional content — Meta will reject or recategorize.
- AUTHENTICATION: only for OTPs/verification codes. Very short, simple body.

Output JSON ONLY (no markdown, no preamble):
{
  "name": "snake_case_name_max_3_words",
  "category": "MARKETING|UTILITY|AUTHENTICATION",
  "header_type": "none|text",
  "header_text": "max 60 chars if header_type is text",
  "body_text": "main message, use {{1}} {{2}} for variables, max 1024 chars",
  "footer_text": "optional short footer max 60 chars",
  "buttons": [
    {"type": "QUICK_REPLY|URL|PHONE_NUMBER", "text": "label", "url": "if URL", "phone_number": "if PHONE_NUMBER"}
  ],
  "body_examples": ["example for {{1}}", "example for {{2}}"],
  "reasoning": "1 sentence why this category"
}`;

async function callAI(prompt) {
  const cfg = loadAiConfig();
  const provider = AI_PROVIDERS.find(p => p.id === cfg.provider) || AI_PROVIDERS[0];
  const model = (cfg.model === 'custom' ? cfg.customModel : cfg.model) || provider.defaultModel;
  const apiKey = cfg.apiKey || '';
  const baseUrl = cfg.baseUrl || provider.baseUrl;

  // ── Anthropic native format ──────────────────────────────
  if (provider.id === 'anthropic') {
    const res = await fetch(baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model,
        max_tokens: 1000,
        system: SYSTEM_PROMPT,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error?.message || `Anthropic API error ${res.status}`);
    }
    const data = await res.json();
    const text = data.content?.[0]?.text || '';
    const clean = text.replace(/^```json\s*/i, '').replace(/\s*```$/, '').trim();
    return JSON.parse(clean);
  }

  // ── OpenAI-compatible format (Google, OpenRouter, Ollama, Nginx, Custom) ──
  const headers = { 'Content-Type': 'application/json' };

  if (provider.id === 'google_ai_studio') {
    // Google AI Studio OpenAI-compat uses ?key= param
    const url = `${baseUrl}?key=${apiKey}`;
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model,
        max_tokens: 1000,
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user',   content: prompt },
        ],
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error?.message || `Google AI Studio error ${res.status}`);
    }
    const data = await res.json();
    const text = data.choices?.[0]?.message?.content || '';
    const clean = text.replace(/^```json\s*/i, '').replace(/\s*```$/, '').trim();
    return JSON.parse(clean);
  }

  if (provider.id === 'nginx_protected') {
    // Use the custom header name + value
    const headerName = Object.keys(cfg.extraHeaders || {})[0] || 'X-Auth-Token';
    headers[headerName] = apiKey;
  } else if (provider.id === 'openrouter') {
    headers['Authorization'] = `Bearer ${apiKey}`;
    headers['HTTP-Referer']  = window.location.origin;
    headers['X-Title']       = 'WA Dashboard';
  } else if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }

  // Merge any extra custom headers
  if (cfg.extraHeaders && typeof cfg.extraHeaders === 'object') {
    Object.entries(cfg.extraHeaders).forEach(([k, v]) => { if (k && v) headers[k] = v; });
  }

  const res = await fetch(baseUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model,
      max_tokens: 1000,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user',   content: prompt },
      ],
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `${provider.label} API error ${res.status}`);
  }

  const data = await res.json();
  const text = data.choices?.[0]?.message?.content || '';
  const clean = text.replace(/^```json\s*/i, '').replace(/\s*```$/, '').trim();
  return JSON.parse(clean);
}

// Expose test helper for Settings page
window.__testAiProvider = async (providerId, apiKey, model, baseUrl, extraHeaders, headerName) => {
  try {
    const provider = AI_PROVIDERS.find(p => p.id === providerId);
    const url = providerId === 'google_ai_studio'
      ? `${baseUrl || provider.baseUrl}?key=${apiKey}`
      : (baseUrl || provider.baseUrl);

    const headers = { 'Content-Type': 'application/json' };
    if (providerId === 'nginx_protected') headers[headerName || 'X-Auth-Token'] = apiKey;
    else if (providerId === 'openrouter') { headers['Authorization'] = `Bearer ${apiKey}`; headers['HTTP-Referer'] = window.location.origin; }
    else if (providerId === 'anthropic') { headers['x-api-key'] = apiKey; headers['anthropic-version'] = '2023-06-01'; headers['anthropic-dangerous-direct-browser-access'] = 'true'; }
    else if (apiKey && providerId !== 'google_ai_studio') headers['Authorization'] = `Bearer ${apiKey}`;

    const body = providerId === 'anthropic'
      ? JSON.stringify({ model, max_tokens: 10, messages: [{ role: 'user', content: 'hi' }] })
      : JSON.stringify({ model, max_tokens: 10, messages: [{ role: 'user', content: 'hi' }] });

    const res = await fetch(url, { method: 'POST', headers, body });
    if (res.ok || res.status === 400) return { ok: true, msg: `${provider.label} reachable ✓` };
    const err = await res.json().catch(() => ({}));
    return { ok: false, msg: err?.error?.message || `HTTP ${res.status}` };
  } catch (e) {
    return { ok: false, msg: e.message };
  }
};

// ── AI Generate Section Component ───────────────────────
function AiGenerateSection({ onApply }) {
  const [prompt, setPrompt]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [result, setResult]     = useState(null);
  const [open, setOpen]         = useState(false);

  const cfg      = loadAiConfig();
  const provider = AI_PROVIDERS.find(p => p.id === cfg.provider) || AI_PROVIDERS[0];
  const hasKey   = cfg.apiKey || cfg.provider === 'ollama';

  const QUICK_PROMPTS = [
    { label: '📦 Order confirmed', text: 'Order confirmation with order ID and estimated delivery date' },
    { label: '🔐 OTP login',       text: 'One-time password for login verification, expires in 10 minutes' },
    { label: '🛍 Flash sale',       text: 'Flash sale promotion with 30% off, limited time offer' },
    { label: '🚚 Shipping update',  text: 'Shipment dispatched notification with tracking link' },
    { label: '📅 Appointment',     text: 'Appointment reminder with date, time and doctor name' },
    { label: '💳 Payment done',    text: 'Payment received confirmation with amount and transaction ID' },
  ];

  const generate = async () => {
    if (!prompt.trim()) return;
    if (!hasKey) {
      setError(`No API key configured. Go to ⚙️ Settings to set up ${provider.label}.`);
      return;
    }
    setLoading(true); setError(''); setResult(null);
    try {
      const data = await callAI(prompt.trim());
      setResult(data);
    } catch (e) {
      setError(e.message || 'Generation failed');
    }
    setLoading(false);
  };

  const apply = () => {
    if (!result) return;
    onApply(result);
    setResult(null);
    setPrompt('');
    setOpen(false);
  };

  return (
    <div style={{
      border: '1px solid rgba(79,209,197,0.3)',
      borderRadius: 'var(--r)',
      marginBottom: 20,
      overflow: 'hidden',
      background: 'rgba(79,209,197,0.03)',
    }}>
      {/* Header toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'transparent', border: 'none',
          padding: '12px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', cursor: 'pointer',
          color: 'var(--accent2)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.05em' }}>
          <span style={{ fontSize: 16 }}>✨</span>
          GENERATE WITH AI
          {!hasKey && <span style={{ fontSize: 10, color: 'var(--warn)', marginLeft: 6 }}>⚠ API key required</span>}
          {hasKey && <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 6, opacity: 0.8 }}>{provider.icon} {provider.label}</span>}
        </span>
        <span style={{ color: 'var(--text3)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.6 }}>
            Describe your template in plain language. AI will suggest the right category, text and buttons.
          </p>

          {/* Quick prompts */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {QUICK_PROMPTS.map(qp => (
              <button
                key={qp.label}
                onClick={() => setPrompt(qp.text)}
                className="btn-secondary"
                style={{ fontSize: 11, padding: '4px 10px', border: '1px solid var(--border2)' }}
              >
                {qp.label}
              </button>
            ))}
          </div>

          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            rows={3}
            placeholder="e.g. Order confirmation with order ID and delivery date for my e-commerce store"
            style={{ resize: 'vertical', marginBottom: 10 }}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) generate(); }}
          />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Ctrl+Enter to generate</div>

          {/* Category hint */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {CATEGORIES.map(cat => (
              <div key={cat} style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 12,
                background: 'var(--bg3)', border: '1px solid var(--border)',
                color: 'var(--text3)', lineHeight: 1.4,
              }}>
                <strong style={{ color: 'var(--text2)' }}>{cat}</strong>
                <div style={{ fontSize: 10, marginTop: 2, maxWidth: 180 }}>{CATEGORY_HINTS[cat]}</div>
              </div>
            ))}
          </div>

          <button
            className="btn-primary"
            onClick={generate}
            disabled={loading || !prompt.trim()}
            style={{ width: '100%', padding: 11 }}
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="spin-dot" />
                Generating…
              </span>
            ) : '✨ Generate Template'}
          </button>

          {error && (
            <div style={{ marginTop: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--r-sm)', padding: '10px 14px', color: 'var(--danger)', fontSize: 13 }}>
              ⚠ {error}
              {error.includes('API key') && (
                <a href="/settings" style={{ marginLeft: 8, color: 'var(--accent2)', fontSize: 12 }}>Go to Settings →</a>
              )}
            </div>
          )}

          {result && (
            <div style={{ marginTop: 14, border: '1px solid rgba(37,211,102,0.3)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
              <div style={{ background: 'rgba(37,211,102,0.08)', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.05em' }}>
                  ✅ AI SUGGESTION
                </span>
                <span style={{ fontSize: 11, background: 'rgba(37,211,102,0.15)', color: 'var(--accent)', padding: '2px 10px', borderRadius: 12 }}>
                  {result.category}
                </span>
              </div>
              <div style={{ padding: '14px', fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ color: 'var(--text3)', fontSize: 11 }}>NAME: </span>
                  <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{result.name}</span>
                </div>
                {result.header_text && (
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: 'var(--text3)', fontSize: 11 }}>HEADER: </span>
                    <strong style={{ color: 'var(--text)' }}>{result.header_text}</strong>
                  </div>
                )}
                <div style={{ background: 'var(--bg3)', borderRadius: 'var(--r-sm)', padding: '10px 12px', color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 8, fontSize: 13 }}>
                  {result.body_text}
                </div>
                {result.footer_text && (
                  <div style={{ marginBottom: 8, color: 'var(--text3)', fontStyle: 'italic', fontSize: 12 }}>{result.footer_text}</div>
                )}
                {result.buttons?.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {result.buttons.map((b, i) => (
                      <span key={i} style={{ fontSize: 11, border: '1px solid var(--accent2)', borderRadius: 6, padding: '3px 10px', color: 'var(--accent2)' }}>
                        {b.type === 'URL' ? '🔗' : b.type === 'PHONE_NUMBER' ? '📞' : '↩'} {b.text}
                      </span>
                    ))}
                  </div>
                )}
                {result.reasoning && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic', marginBottom: 12, padding: '6px 10px', background: 'var(--bg4)', borderRadius: 'var(--r-sm)' }}>
                    💡 {result.reasoning}
                  </div>
                )}
                <button className="btn-primary" onClick={apply} style={{ width: '100%', padding: 10 }}>
                  ← Apply to Form & Edit
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────
export default function Templates() {
  const { activeNumber } = useNumbers();
  const [templates, setTemplates]     = useState([]);
  const [syncing, setSyncing]         = useState(false);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState(null);
  const [showCreate, setShowCreate]   = useState(false);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [creating, setCreating]       = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createError, setCreateError] = useState('');
  const [preview, setPreview]         = useState('');
  const [activeView, setActiveView]   = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => { loadTemplates(); }, []);

  // Live preview
  useEffect(() => {
    let p = '';
    if (form.header_type === 'text' && form.header_text) p += `*${form.header_text}*\n\n`;
    if (form.body_text) p += form.body_text + '\n';
    if (form.footer_text) p += `\n_${form.footer_text}_`;
    if (form.buttons.length > 0) {
      p += '\n\n' + form.buttons.filter(b => b.text).map(b => `[ ${b.text} ]`).join('  ');
    }
    setPreview(p);
  }, [form]);

  const varCount = (form.body_text.match(/\{\{\d+\}\}/g) || []).length;

  const loadTemplates = async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get('/templates' + (activeNumber ? '?wa_number_id=' + activeNumber.id : '')); setTemplates(r.data); } catch (e) {}
    if (!silent) setLoading(false);
  };

  const sync = async () => {
    if (!activeNumber) return alert('Select a number first');
    setSyncing(true);
    try {
      const r = await api.get('/templates/sync?wa_number_id=' + activeNumber.id);
      await loadTemplates(true);
      alert(`✅ Synced ${r.data.synced ?? 0} template(s) from WABA: ${r.data.business_account_id}\n\nNote: Templates are shared across all phone numbers under the same Business Account.`);
    } catch (e) { alert(e.response?.data?.error || 'Sync failed'); }
    setSyncing(false);
  };

  const submitCreate = async () => {
    setCreating(true); setCreateResult(null); setCreateError('');
    try {
      const payload = { ...form };
      payload.body_examples = form.body_examples.filter(e => e.trim());
      const r = await api.post('/templates/create', payload);
      setCreateResult(r.data);
      loadTemplates();
    } catch (e) {
      const d = e.response?.data;
      setCreateError((d?.error || 'Create failed') + (d?.error_code ? ` (code: ${d.error_code})` : ''));
    }
    setCreating(false);
  };

  const deleteTemplate = async (name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/templates/${name}?wa_number_id=${activeNumber?.id}`);
      loadTemplates();
    } catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addButton = () => {
    if (form.buttons.length >= 3) return;
    setForm(f => ({ ...f, buttons: [...f.buttons, { type: 'QUICK_REPLY', text: '', url: '', phone_number: '' }] }));
  };
  const updateButton = (i, k, v) => {
    const btns = [...form.buttons]; btns[i] = { ...btns[i], [k]: v };
    setForm(f => ({ ...f, buttons: btns }));
  };
  const removeButton = (i) => setForm(f => ({ ...f, buttons: f.buttons.filter((_, j) => j !== i) }));

  // Apply AI result to form
  const applyAiResult = useCallback((result) => {
    setForm({
      name:         (result.name || '').toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').slice(0, 512),
      category:     CATEGORIES.includes(result.category) ? result.category : 'UTILITY',
      language:     'en_US',
      header_type:  result.header_type === 'text' ? 'text' : 'none',
      header_text:  result.header_text || '',
      body_text:    result.body_text || '',
      footer_text:  result.footer_text || '',
      body_examples: Array.isArray(result.body_examples) ? result.body_examples : [],
      buttons:      Array.isArray(result.buttons)
        ? result.buttons.slice(0, 3).map(b => ({
            type:         b.type || 'QUICK_REPLY',
            text:         b.text || '',
            url:          b.url || '',
            phone_number: b.phone_number || '',
          }))
        : [],
    });
    setCreateResult(null);
    setCreateError('');
    setTimeout(() => document.getElementById('template-form-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  // ── Filtered list ──────────────────────────────────────
  const filtered = templates.filter(t => {
    const catMatch    = activeView === 'ALL' || t.category === activeView;
    const statusMatch = statusFilter === 'ALL' || t.status === statusFilter;
    return catMatch && statusMatch;
  });

  const counts = {
    ALL:            templates.length,
    MARKETING:      templates.filter(t => t.category === 'MARKETING').length,
    UTILITY:        templates.filter(t => t.category === 'UTILITY').length,
    AUTHENTICATION: templates.filter(t => t.category === 'AUTHENTICATION').length,
  };

  const allStatuses = ['ALL', ...Array.from(new Set(templates.map(t => t.status))).sort()];

  return (
    <div style={{ padding: '20px 16px', maxWidth: 760, margin: '0 auto' }}>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, letterSpacing: '0.06em', color: 'var(--text2)' }}>
          TEMPLATES
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={sync} disabled={syncing} style={{ fontSize: 13 }}>
            {syncing ? 'Syncing…' : '↻ Sync from Meta'}
          </button>
          <button
            className="btn-primary"
            onClick={() => { setShowCreate(true); setCreateResult(null); setCreateError(''); setForm(EMPTY_FORM); }}
            style={{ fontSize: 13 }}
          >
            + Create Template
          </button>
        </div>
      </div>

      {/* WABA info banner */}
      {activeNumber && (
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>📋</span>
          <span>Templates belong to <strong style={{ color: 'var(--text2)' }}>Business Account (WABA)</strong> — not to individual phone numbers.</span>
          <span style={{ marginLeft: 4 }}>WABA ID: <code style={{ color: 'var(--accent)', fontSize: 11 }}>{activeNumber.business_account_id}</code></span>
          <span style={{ color: 'var(--text3)' }}>· All numbers under this WABA share these templates.</span>
        </div>
      )}

      {/* ── Create Form ── */}
      {showCreate && (
        <div id="template-form-top" className="card" style={{ marginBottom: 24, borderColor: 'var(--accent2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.05em' }}>
              // CREATE & SUBMIT TEMPLATE
            </div>
            <button className="btn-ghost" onClick={() => setShowCreate(false)}>✕</button>
          </div>

          {/* ── AI Generate Section ── */}
          <AiGenerateSection onApply={applyAiResult} />

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {/* ── Form Column ── */}
            <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>

              <div>
                <label className="label">Template Name</label>
                <input
                  value={form.name}
                  onChange={e => setField('name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="order_confirmation"
                />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Lowercase, numbers, underscores only</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="label">Category</label>
                  <select value={form.category} onChange={e => setField('category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Language</label>
                  <select value={form.language} onChange={e => setField('language', e.target.value)}>
                    {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Category guidance */}
              <div style={{ fontSize: 11, color: 'var(--text3)', background: 'var(--bg3)', borderRadius: 'var(--r-sm)', padding: '8px 12px', lineHeight: 1.6 }}>
                {form.category === 'MARKETING' && '🛍 MARKETING — promotions, offers, product launches. Can include CTAs.'}
                {form.category === 'UTILITY'   && '📦 UTILITY — transactional only (order updates, alerts). Must be strictly non-promotional.'}
                {form.category === 'AUTHENTICATION' && '🔐 AUTHENTICATION — OTPs & verification codes only. Short, simple format required.'}
              </div>

              <div>
                <label className="label">Header (Optional)</label>
                <select value={form.header_type} onChange={e => setField('header_type', e.target.value)} style={{ marginBottom: 8 }}>
                  <option value="none">No header</option>
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                </select>
                {form.header_type === 'text' && (
                  <input
                    value={form.header_text}
                    onChange={e => setField('header_text', e.target.value)}
                    placeholder="Header text (max 60 chars)"
                    maxLength={60}
                  />
                )}
                {['image','video','document'].includes(form.header_type) && (
                  <div style={{ fontSize: 12, color: 'var(--warn)', padding: '8px 12px', background: 'rgba(245,158,11,0.1)', borderRadius: 'var(--r-sm)', border: '1px solid rgba(245,158,11,0.3)' }}>
                    ⚠ Media header — Meta will ask for a sample during review. Template can be submitted without it but approval may be slower.
                  </div>
                )}
              </div>

              <div>
                <label className="label">Body Text * (required)</label>
                <textarea
                  value={form.body_text}
                  onChange={e => setField('body_text', e.target.value)}
                  rows={4}
                  placeholder={"Hi {{1}}, your order {{2}} is confirmed!\n\nUse {{1}}, {{2}} for variables."}
                  style={{ resize: 'vertical' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  <span>Use {"{{1}}"}, {"{{2}}"} for variables. Detected: <strong style={{ color: varCount > 0 ? 'var(--accent2)' : 'inherit' }}>{varCount} variable(s)</strong></span>
                  <span style={{ color: form.body_text.length > 900 ? 'var(--warn)' : 'inherit' }}>{form.body_text.length}/1024</span>
                </div>
              </div>

              {varCount > 0 && (
                <div>
                  <label className="label">Variable Examples (recommended for approval)</label>
                  {Array.from({ length: varCount }).map((_, i) => (
                    <input
                      key={i}
                      value={form.body_examples[i] || ''}
                      onChange={e => {
                        const ex = [...form.body_examples];
                        ex[i] = e.target.value;
                        setField('body_examples', ex);
                      }}
                      placeholder={`Example for {{${i + 1}}}`}
                      style={{ marginBottom: 6 }}
                    />
                  ))}
                </div>
              )}

              <div>
                <label className="label">Footer (Optional)</label>
                <input
                  value={form.footer_text}
                  onChange={e => setField('footer_text', e.target.value)}
                  placeholder="Reply STOP to unsubscribe"
                  maxLength={60}
                />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, textAlign: 'right' }}>
                  {form.footer_text.length}/60
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label className="label" style={{ margin: 0 }}>Buttons (Optional, max 3)</label>
                  {form.buttons.length < 3 && (
                    <button className="btn-secondary" onClick={addButton} style={{ fontSize: 11, padding: '4px 10px' }}>+ Add</button>
                  )}
                </div>
                {form.buttons.map((btn, i) => (
                  <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: 10, marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center', marginBottom: btn.type !== 'QUICK_REPLY' ? 8 : 0 }}>
                      <select value={btn.type} onChange={e => updateButton(i, 'type', e.target.value)}>
                        <option value="QUICK_REPLY">Quick Reply</option>
                        <option value="URL">URL</option>
                        <option value="PHONE_NUMBER">Phone</option>
                      </select>
                      <input value={btn.text} onChange={e => updateButton(i, 'text', e.target.value)} placeholder="Button label" />
                      <button className="btn-ghost" onClick={() => removeButton(i)} style={{ color: 'var(--danger)', padding: '4px 6px' }}>✕</button>
                    </div>
                    {btn.type === 'URL' && (
                      <input value={btn.url} onChange={e => updateButton(i, 'url', e.target.value)} placeholder="https://example.com" />
                    )}
                    {btn.type === 'PHONE_NUMBER' && (
                      <input value={btn.phone_number} onChange={e => updateButton(i, 'phone_number', e.target.value)} placeholder="+911234567890" />
                    )}
                  </div>
                ))}
              </div>

              {createError && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--r-sm)', padding: '10px 14px', color: 'var(--danger)', fontSize: 13 }}>
                  ⚠ {createError}
                </div>
              )}
              {createResult && (
                <div style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid var(--accent)', borderRadius: 'var(--r-sm)', padding: '12px 14px', fontSize: 13 }}>
                  <div style={{ color: 'var(--accent)', fontWeight: 600, marginBottom: 4 }}>✅ Submitted to Meta!</div>
                  <div style={{ color: 'var(--text2)' }}>Status: <span style={{ color: 'var(--warn)' }}>{createResult.status || 'PENDING'}</span></div>
                  <div style={{ color: 'var(--text3)', fontSize: 12, marginTop: 4 }}>Meta usually reviews within a few hours. Click "Sync" to check status.</div>
                  <button
                    className="btn-secondary"
                    onClick={() => { setForm(EMPTY_FORM); setCreateResult(null); }}
                    style={{ marginTop: 10, fontSize: 12 }}
                  >
                    + Create Another
                  </button>
                </div>
              )}

              {!createResult && (
                <button
                  className="btn-primary"
                  onClick={submitCreate}
                  disabled={creating || !form.name || !form.body_text}
                  style={{ padding: '12px', fontSize: 15 }}
                >
                  {creating ? 'Submitting…' : '🚀 Submit for Approval'}
                </button>
              )}
            </div>

            {/* ── Preview Column ── */}
            <div style={{ width: 240, flexShrink: 0 }}>
              <label className="label">Live Preview</label>
              <div style={{ background: '#0b2118', border: '1px solid #1a3828', borderRadius: 12, padding: 16, minHeight: 120 }}>
                {preview ? (
                  <div style={{ background: '#1a4a2e', borderRadius: '12px 12px 12px 4px', padding: '10px 14px', fontSize: 13, color: '#e8f5e9', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {preview}
                  </div>
                ) : (
                  <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', marginTop: 20 }}>Fill the form to see preview</div>
                )}
              </div>
              <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text3)', lineHeight: 1.7 }}>
                <div>• <span style={{ color: 'var(--text2)' }}>MARKETING</span> — promotions, offers</div>
                <div>• <span style={{ color: 'var(--text2)' }}>UTILITY</span> — order updates, alerts</div>
                <div>• <span style={{ color: 'var(--text2)' }}>AUTH</span> — OTP, verification</div>
                <div style={{ marginTop: 8, color: 'var(--warn)' }}>⚡ Approval: usually few hours</div>
              </div>

              {/* API Key status in preview panel */}
              <div style={{ marginTop: 16, padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--r-sm)', fontSize: 11 }}>
                <div style={{ color: 'var(--text3)', marginBottom: 4 }}>AI Generation:</div>
                {(() => {
                  const c = loadAiConfig();
                  const p = AI_PROVIDERS.find(x => x.id === c.provider) || AI_PROVIDERS[0];
                  const ok = c.apiKey || c.provider === 'ollama';
                  return ok ? (
                    <span style={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }}></span>
                      {p.icon} {p.label}
                    </span>
                  ) : (
                    <a href="/settings" style={{ color: 'var(--warn)', fontSize: 11 }}>⚙ Configure AI in Settings</a>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Category Tabs + Status Filter ── */}
      {!loading && templates.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
            {VIEW_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveView(tab)}
                style={{
                  padding: '5px 14px', borderRadius: 20, border: '1px solid', fontSize: 12,
                  fontFamily: 'var(--mono)', cursor: 'pointer', transition: 'all 0.15s',
                  borderColor: activeView === tab ? 'var(--accent)' : 'var(--border)',
                  background:  activeView === tab ? 'rgba(37,211,102,0.12)' : 'var(--bg2)',
                  color:       activeView === tab ? 'var(--accent)' : 'var(--text2)',
                }}
              >
                {tab} <span style={{ opacity: 0.6, fontSize: 10 }}>({counts[tab] ?? 0})</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>STATUS:</span>
            {allStatuses.map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                style={{
                  padding: '3px 10px', borderRadius: 12, border: '1px solid', fontSize: 11,
                  cursor: 'pointer', transition: 'all 0.15s',
                  borderColor: statusFilter === s ? 'var(--accent2)' : 'var(--border)',
                  background:  statusFilter === s ? 'rgba(79,209,197,0.1)' : 'transparent',
                  color:       statusFilter === s ? 'var(--accent2)' : 'var(--text3)',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Templates List ── */}
      {loading && <div style={{ color: 'var(--text2)', padding: 20 }}>Loading…</div>}

      {!loading && templates.length === 0 && !showCreate && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ marginBottom: 16 }}>No templates yet. Create one or sync from Meta.</div>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Create Template</button>
        </div>
      )}

      {!loading && templates.length > 0 && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 13 }}>
          No templates match this filter.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map(t => {
          let components = [];
          try { components = JSON.parse(t.components || '[]'); } catch {}
          const header  = components.find(c => c.type === 'HEADER');
          const body    = components.find(c => c.type === 'BODY');
          const footer  = components.find(c => c.type === 'FOOTER');
          const buttons = components.find(c => c.type === 'BUTTONS');
          const isOpen  = expanded === t.id;

          return (
            <div key={t.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                onClick={() => setExpanded(isOpen ? null : t.id)}
                style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
              >
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text)', flex: 1, minWidth: 100 }}>{t.name}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className={`tag ${statusTag(t.status)}`}>{t.status}</span>
                  <span className="tag tag-gray">{t.category}</span>
                  <span className="tag tag-gray">{t.language}</span>
                  {t.quality_score && (
                    <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: qualityColor(t.quality_score), border: `1px solid ${qualityColor(t.quality_score)}`, borderRadius: 10, padding: '1px 7px' }}>
                      ★ {t.quality_score}
                    </span>
                  )}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); deleteTemplate(t.name); }}
                  className="btn-ghost"
                  style={{ color: 'var(--danger)', fontSize: 14, padding: '2px 6px' }}
                >🗑</button>
                <span style={{ color: 'var(--text3)', fontSize: 12 }}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {isOpen && (
                <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg3)', fontSize: 13 }}>
                  {t.meta_id && (
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 10 }}>
                      Meta ID: {t.meta_id}
                    </div>
                  )}

                  {t.correct_category && t.correct_category !== t.category && (
                    <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 'var(--r-sm)', padding: '8px 12px', marginBottom: 10, fontSize: 12, color: 'var(--warn)' }}>
                      ⚠ Meta suggests recategorizing to <strong>{t.correct_category}</strong>
                    </div>
                  )}

                  {header && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginRight: 8 }}>HEADER ({header.format})</span>
                      {header.text && <span style={{ color: 'var(--text2)', fontWeight: 600 }}>{header.text}</span>}
                      {!header.text && <span style={{ color: 'var(--text3)' }}>[{header.format} media]</span>}
                    </div>
                  )}

                  {body && (
                    <div style={{ color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 8 }}>
                      {body.text}
                    </div>
                  )}

                  {footer && (
                    <div style={{ color: 'var(--text3)', fontSize: 12, fontStyle: 'italic', marginBottom: 8 }}>{footer.text}</div>
                  )}

                  {buttons?.buttons?.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {buttons.buttons.map((b, i) => (
                        <span key={i} style={{ fontSize: 11, border: '1px solid var(--accent2)', borderRadius: 6, padding: '2px 10px', color: 'var(--accent2)' }}>
                          {b.type === 'URL' ? '🔗' : b.type === 'PHONE_NUMBER' ? '📞' : '↩'} {b.text}
                        </span>
                      ))}
                    </div>
                  )}

                  {t.synced_at && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 10, fontFamily: 'var(--mono)' }}>
                      Last synced: {new Date(t.synced_at).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
